import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, Users, Baby, Calendar as CalendarIcon } from "lucide-react";
import { analysesApi, projectsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import html2canvas from "html2canvas";

const VITALIST_BG = "https://static.prod-images.emergentagent.com/jobs/9ad6fcf0-f300-480e-a17c-fa8b74dce387/images/4cc9af13cacd7335112e68a81b69f72e4e3d0b575a70f5f0109aa3011d3a0742.png";

export default function Infographics() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [project, setProject] = useState(null);
  const refA = useRef(null);
  const refB = useRef(null);
  const refC = useRef(null);

  useEffect(() => {
    Promise.all([analysesApi.summary(projectId), projectsApi.get(projectId)]).then(([s, p]) => {
      setData(s); setProject(p);
    });
  }, [projectId]);

  const exportPng = async (ref, name) => {
    if (!ref.current) return;
    try {
      const canvas = await html2canvas(ref.current, {
        scale: 3, // ≈ 300 DPI for 1000px → 3000px
        backgroundColor: "#0D1B2A",
        useCORS: true, logging: false,
      });
      const link = document.createElement("a");
      link.download = `${name}-${project.country}-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png", 1.0);
      link.click();
      toast.success("Infographie exportée (PNG 300 DPI)");
    } catch (e) {
      toast.error("Erreur export : " + e.message);
    }
  };

  if (!data || !project) return <div className="p-10 label-mono text-xs text-muted-foreground">Chargement…</div>;

  const v = data.vitaliste || {};

  return (
    <div className="px-6 lg:px-10 py-8 max-w-6xl mx-auto w-full" data-testid="infographics-page">
      <div className="label-mono text-[10px] text-muted-foreground">INFOGRAPHIES PARTAGEABLES</div>
      <h1 className="mt-1 font-serif font-bold text-4xl">Rendez visible l'invisible</h1>
      <p className="mt-2 text-muted-foreground text-sm max-w-2xl">
        Trois infographies conçues pour la diffusion citoyenne — format poster haute résolution (PNG 300 DPI).
        Partagez-les sur les réseaux, affichez-les en audition parlementaire, publiez-les dans la presse.
      </p>

      <Tabs defaultValue="A" className="mt-8" data-testid="infographics-tabs">
        <TabsList className="grid grid-cols-3 w-full max-w-xl">
          <TabsTrigger value="A" data-testid="tab-infographic-a">Poids en vies</TabsTrigger>
          <TabsTrigger value="B" data-testid="tab-infographic-b">Enfant endetté</TabsTrigger>
          <TabsTrigger value="C" data-testid="tab-infographic-c">Jour de libération</TabsTrigger>
        </TabsList>

        <TabsContent value="A">
          <ExportBar onClick={() => exportPng(refA, "poids-vies")} testId="export-infographic-a" />
          <InfographicPoidsVies innerRef={refA} v={v} project={project} />
        </TabsContent>
        <TabsContent value="B">
          <ExportBar onClick={() => exportPng(refB, "enfant-endette")} testId="export-infographic-b" />
          <InfographicEnfantEndette innerRef={refB} v={v} project={project} />
        </TabsContent>
        <TabsContent value="C">
          <ExportBar onClick={() => exportPng(refC, "jour-liberation")} testId="export-infographic-c" />
          <InfographicJourLiberation innerRef={refC} v={v} project={project} data={data} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ExportBar({ onClick, testId }) {
  return (
    <div className="flex justify-end my-4">
      <Button onClick={onClick} className="bg-marine hover:bg-marine/90 text-white" data-testid={testId}>
        <Download size={14} className="mr-2" /> Exporter PNG 300 DPI
      </Button>
    </div>
  );
}

function InfographicPoidsVies({ innerRef, v, project }) {
  const days = v.D2 ? Math.round(v.D2) : null;
  // Capped silhouettes count for visual — 1 silhouette = 10 days, max 100
  const silhouettes = days ? Math.min(100, Math.ceil(days / 10)) : 0;

  return (
    <div ref={innerRef} className="relative bg-[#0D1B2A] text-white aspect-[4/5] max-w-3xl mx-auto rounded-sm overflow-hidden p-12"
         style={{ backgroundImage: `linear-gradient(#0D1B2A, rgba(13,27,42,0.85))` }}>
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: `url(${VITALIST_BG})`, backgroundSize: "cover" }} />
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div className="label-mono text-[11px] text-[#C9A84C]">DEBT-ANALYZER PRO</div>
          <div className="label-mono text-[10px] text-white/60">{project.country} · {project.period_end}</div>
        </div>

        <h2 className="mt-8 font-serif font-black leading-[0.95] text-5xl">
          Le poids de la dette<br />
          <span className="text-[#C9A84C]">en vies humaines.</span>
        </h2>

        <div className="mt-10 border-l-4 border-[#C9A84C] pl-6">
          <div className="label-mono text-[10px] text-white/60">CHAQUE CONTRIBUABLE ACTIF HYPOTHÈQUE</div>
          <div className="mt-3 font-mono text-7xl md:text-8xl font-black tabular-nums">
            {days != null ? days.toLocaleString("fr-FR") : "—"}
          </div>
          <div className="mt-2 font-serif text-2xl">jours de sa vie.</div>
        </div>

        {silhouettes > 0 && (
          <div className="mt-8">
            <div className="grid grid-cols-20 gap-[2px]" style={{ gridTemplateColumns: "repeat(20, minmax(0, 1fr))" }}>
              {Array.from({ length: silhouettes }).map((_, i) => (
                <Silhouette key={i} tint={i < silhouettes / 2 ? "#C9A84C" : "#C0392B"} />
              ))}
            </div>
            <div className="mt-3 label-mono text-[9px] text-white/50">
              1 silhouette ≈ 10 jours de vie · {silhouettes} silhouettes visibles
            </div>
          </div>
        )}

        <div className="mt-auto pt-8 border-t border-white/10 flex items-center justify-between gap-6">
          <blockquote className="italic font-serif text-lg text-white/90 max-w-md">
            « Chaque dollar emprunté est une vie hypothéquée. »
          </blockquote>
          <div className="label-mono text-[9px] text-white/50 text-right">
            Méthodologie D2 · A. ELY MUSTAPHA<br />Théorie Nouvelle des Finances Publiques
          </div>
        </div>
      </div>
    </div>
  );
}

function Silhouette({ tint = "#C9A84C" }) {
  return (
    <svg viewBox="0 0 12 24" className="w-full h-auto" aria-hidden>
      <circle cx="6" cy="4" r="2.5" fill={tint} />
      <path d="M1.5 22 C1.5 14, 10.5 14, 10.5 22 L10.5 23 L1.5 23 Z" fill={tint} />
    </svg>
  );
}

function InfographicEnfantEndette({ innerRef, v, project }) {
  const days = v.V6 ? Math.round(v.V6) : null;
  return (
    <div ref={innerRef} className="relative bg-[#0D1B2A] text-white aspect-[4/5] max-w-3xl mx-auto rounded-sm overflow-hidden p-12">
      <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${VITALIST_BG})`, backgroundSize: "cover" }} />
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div className="label-mono text-[11px] text-[#C9A84C]">L'ÉQUATION DE L'ENFANT ENDETTÉ · V6</div>
          <div className="label-mono text-[10px] text-white/60">{project.country}</div>
        </div>

        <div className="mt-12 text-center">
          <Baby size={80} className="mx-auto text-[#C9A84C]" strokeWidth={1.3} />
          <h2 className="mt-8 font-serif font-black text-4xl md:text-5xl leading-tight">
            Un enfant né en {project.period_end} en {project.country}<br />
            hérite déjà de
          </h2>
          <div className="mt-8 font-mono text-7xl md:text-8xl font-black text-[#C0392B] tabular-nums">
            {days != null ? days.toLocaleString("fr-FR") : "—"}
          </div>
          <div className="mt-2 font-serif text-2xl">jours de vie hypothéqués.</div>
          <p className="mt-8 font-serif italic text-lg text-white/80 max-w-2xl mx-auto">
            « Sans avoir pu dire oui. »
          </p>
        </div>

        <div className="mt-auto pt-8 border-t border-white/10 text-center">
          <div className="font-mono text-xs text-white/60">
            V6 = (Dette + Déficit projeté 10 ans) / (Pop. active future × Sj futur)
          </div>
          <div className="mt-4 label-mono text-[9px] text-white/50">
            DEBT-ANALYZER PRO · A. ELY MUSTAPHA — Bookedition
          </div>
        </div>
      </div>
    </div>
  );
}

function InfographicJourLiberation({ innerRef, v, project, data }) {
  // Jour de libération de la dette = date à partir de laquelle le contribuable
  // ne finance plus la dette. Si D5=90j sur 365, libération à j+90 = 1er avril environ.
  const d5 = v.D5;
  const dayOfYear = d5 ? Math.min(365, Math.round(d5)) : null;
  const freedomDate = dayOfYear ? new Date(2026, 0, 1 + dayOfYear) : null;
  const freedomStr = freedomDate ? freedomDate.toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) : null;

  return (
    <div ref={innerRef} className="relative bg-[#0D1B2A] text-white aspect-[4/5] max-w-3xl mx-auto rounded-sm overflow-hidden p-12">
      <div className="absolute inset-0 opacity-15" style={{ backgroundImage: `url(${VITALIST_BG})`, backgroundSize: "cover" }} />
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex items-center justify-between">
          <div className="label-mono text-[11px] text-[#C9A84C]">JOUR DE LIBÉRATION DE LA DETTE</div>
          <div className="label-mono text-[10px] text-white/60">{project.country} · {project.period_end}</div>
        </div>

        <h2 className="mt-10 font-serif font-black text-4xl md:text-5xl">
          À partir de <span className="text-[#C9A84C]">quelle date</span><br />
          votre travail cesse-t-il de rembourser la dette&nbsp;?
        </h2>

        <div className="mt-10">
          <CalendarIcon size={60} className="text-[#C9A84C]" strokeWidth={1.3} />
          <div className="mt-6 font-mono text-7xl md:text-8xl font-black tabular-nums text-[#C0392B]">
            {freedomStr || "—"}
          </div>
          <div className="mt-2 font-serif text-xl text-white/80">
            {dayOfYear != null ? `Soit ${dayOfYear} jours/an absorbés par le service de la dette` : "Données insuffisantes"}
          </div>
        </div>

        {/* Bar showing year progress */}
        {dayOfYear != null && (
          <div className="mt-8">
            <div className="relative h-5 rounded-sm overflow-hidden border border-white/20">
              <div className="absolute inset-y-0 left-0 bg-[#C0392B]" style={{ width: `${(dayOfYear / 365) * 100}%` }} />
              <div className="absolute inset-y-0 bg-[#27AE60]" style={{ left: `${(dayOfYear / 365) * 100}%`, right: 0 }} />
            </div>
            <div className="mt-2 flex justify-between label-mono text-[9px]">
              <span className="text-[#C0392B]">SERVICE DE LA DETTE · {((dayOfYear / 365) * 100).toFixed(0)}%</span>
              <span className="text-[#27AE60]">ESPACE SOCIAL · {(((365 - dayOfYear) / 365) * 100).toFixed(0)}%</span>
            </div>
          </div>
        )}

        <div className="mt-auto pt-8 border-t border-white/10 text-xs text-white/60 font-mono">
          D5 = (Service annuel / Pop. active) / Sj · DEBT-ANALYZER PRO · A. ELY MUSTAPHA
        </div>
      </div>
    </div>
  );
}
